import React, { useRef } from "react"

import { FieldTooltip } from "@/components/fields/field-tooltip"
import { HStack } from "@/nzds/layout"

import { Stack, Tooltip, Typography } from "@mui/material"
import { useGridApiContext } from "@mui/x-data-grid-pro"

import type { DataGridColumnHeaderProps } from "./data-grid-column-header.types"

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  )
}

export const DataGridColumnHeader: React.FC<DataGridColumnHeaderProps> = ({
  dataType = "string",
  description,
  field,
  headerName,
  subHeaderName,
  tooltipContent,
  unit,
}) => {
  const titleContainerRef = useRef<HTMLDivElement>(null)

  const [isTitleOverflown, setIsTitleOverflown] = React.useState(false)

  const apiRef = useGridApiContext()

  React.useEffect(() => {
    return apiRef.current.subscribeEvent("columnWidthChange", (params) => {
      if (params.colDef.field !== field) {
        return
      }
      if (titleContainerRef.current) {
        setIsTitleOverflown(isOverflown(titleContainerRef.current))
      }
    })
  }, [apiRef, field])

  return (
    <HStack
      gap={1}
      onMouseOver={() => {
        if (titleContainerRef.current) {
          setIsTitleOverflown(isOverflown(titleContainerRef.current))
        }
      }}
      sx={{
        alignItems: "flex-start",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Stack
        sx={{
          alignItems: dataType === "number" ? "flex-end" : undefined,
          overflow: "hidden",
          textAlign: "right",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <HStack sx={{ alignItems: "center", gap: 1 }}>
          {isTitleOverflown ? (
            <Tooltip
              describeChild
              disableInteractive
              placement="bottom"
              title={description ?? headerName}
            >
              <Typography
                component="div"
                noWrap
                ref={titleContainerRef}
                sx={{
                  fontWeight: "fontWeightBold",
                }}
              >
                {headerName}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              component="div"
              noWrap
              ref={titleContainerRef}
              sx={{
                fontWeight: "fontWeightBold",
              }}
            >
              {headerName}
            </Typography>
          )}
          {tooltipContent ? (
            <FieldTooltip content={tooltipContent} tabIndex={-1} />
          ) : null}
        </HStack>

        {unit ? (
          <Typography noWrap variant="body2">
            {unit}
          </Typography>
        ) : null}
        {subHeaderName ? (
          <Typography noWrap variant="body2">
            {subHeaderName}
          </Typography>
        ) : null}
      </Stack>
    </HStack>
  )
}
